import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { X, ArrowUpDown } from "lucide-react";
import { NativeSelectField } from "@/components/native/NativeSelectField";
import type { NativeOption } from "@/components/native/NativeOptionSheet";

interface OrdersFilterProps {
  currentFilter: string;
  appliedPaymentStatus?: "paid" | "pending" | "unpaid" | "overdue";
  appliedSortBy?: string;
  onApplyFilters: (filters: {
    paymentStatus?: "paid" | "pending" | "unpaid" | "overdue";
    sortBy?: string;
  }) => void;
  onClearFilters: () => void;
}

export const OrdersFilter = ({
  currentFilter: _currentFilter,
  appliedPaymentStatus,
  appliedSortBy,
  onApplyFilters,
  onClearFilters,
}: OrdersFilterProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const [paymentStatus, setPaymentStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("updated");

  useEffect(() => {
    if (appliedPaymentStatus) {
      setPaymentStatus(appliedPaymentStatus);
    } else {
      setPaymentStatus("all");
    }
  }, [appliedPaymentStatus]);

  useEffect(() => {
    if (appliedSortBy) {
      setSortBy(appliedSortBy);
    } else {
      setSortBy("updated");
    }
  }, [appliedSortBy]);

  const buildFilters = useCallback(
    (payment: string, sort: string) => {
      const filters: {
        paymentStatus?: "paid" | "pending" | "unpaid" | "overdue";
        sortBy?: string;
      } = {};

      if (payment && payment !== "all") {
        filters.paymentStatus = payment as
          | "paid"
          | "pending"
          | "unpaid"
          | "overdue";
      }

      if (sort && sort !== "updated") {
        filters.sortBy = sort;
      }

      return filters;
    },
    [],
  );

  const handlePaymentChange = (value: string) => {
    setPaymentStatus(value);
    onApplyFilters(buildFilters(value, sortBy));
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    onApplyFilters(buildFilters(paymentStatus, value));
  };

  const handleClear = () => {
    setPaymentStatus("all");
    setSortBy("newest");
    onClearFilters();
  };

  const hasActivePaymentFilter = paymentStatus && paymentStatus !== "all";
  const hasActiveSortFilter = sortBy && sortBy !== "newest";
  const hasActiveFilters = hasActivePaymentFilter || hasActiveSortFilter;

  const paymentOptions: NativeOption[] = [
    { value: "all", label: o.filters.allShort },
    { value: "paid", label: o.filter.paid },
    { value: "pending", label: o.filter.pending },
    { value: "unpaid", label: o.filter.unpaid },
    { value: "overdue", label: o.filter.overdue },
  ];

  const sortOptions: NativeOption[] = [
    { value: "newest", label: o.filter.newest },
    { value: "oldest", label: o.filter.oldest },
    { value: "price-high-low", label: o.filter.priceHighLow },
    { value: "price-low-high", label: o.filter.priceLowHigh },
  ];

  return (
    <div className="flex flex-row gap-4 items-center justify-end mb-2 flex-wrap">
      <div className="flex flex-row items-center gap-2 min-w-[140px]">
        <label className="block text-sm text-primary/60 whitespace-nowrap">
          {o.filter.paymentStatus}
        </label>
        <NativeSelectField
          value={paymentStatus}
          onValueChange={handlePaymentChange}
          options={paymentOptions}
          title={o.filter.paymentStatus}
          placeholder={o.filter.selectPaymentStatus}
          triggerClassName="w-full min-w-[120px]"
        />
      </div>

      <div className="flex items-center gap-2 min-w-[140px]">
        <span className="text-sm text-primary/60 whitespace-nowrap">
          {o.filter.sortBy}
        </span>
        <NativeSelectField
          value={sortBy}
          onValueChange={handleSortChange}
          options={sortOptions}
          title={o.filter.sortBy}
          triggerClassName="w-[160px] bg-card"
          renderOptionLabel={(opt) => (
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4" />
              {opt.label}
            </div>
          )}
        />
      </div>

      <div className="flex backdrop-blur gap-2">
        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            {o.actions.clear}
          </Button>
        )}
      </div>
    </div>
  );
};
